import {jsx} from 'panel';

import {Bar, BarValueType} from 'common/charts/visualization/bar-viz/types';
import {getBarBackground, getBarChartDisplayValues} from 'common/charts/visualization/bar-viz/util';

// cannot have negative values with a stacked bar
function isBarValid(bar: Bar, hasNegativeValues: boolean) {
  if (bar.segments.length > 1) {
    if (hasNegativeValues) {
      return false;
    }

    if (bar.segments.some((segment) => segment.value < 0)) {
      return false;
    }
  }

  return true;
}

export function renderBarWithLabel(
  bar: Bar,
  {
    hasNegativeValues,
    barValueType,
    chartMax,
    chartMin,
    showSegmentLabels,
    enableClick,
    isFirstOfPair,
  }: {
    hasNegativeValues: boolean;
    barValueType: BarValueType;
    chartMax: number;
    chartMin: number;
    showSegmentLabels: boolean;
    enableClick: boolean;
    isFirstOfPair: boolean;
  },
  {mouseMove, mouseLeave, clickBar},
) {
  if (!isBarValid(bar, hasNegativeValues)) {
    throw new Error(`Invalid bar configuration: ${bar}`);
  }

  const {segments} = bar;
  const barLabel = bar.label ?? (!showSegmentLabels ? segments[0].label : null);
  const barIsNegative = bar.segments[0].value < 0;
  const segmentMax = barIsNegative ? chartMin : chartMax;

  let barGridColumn = `1`;
  if (hasNegativeValues && !barIsNegative) {
    barGridColumn = `2`;
  }

  return (
    <div
      sel='.chart-bar'
      class={{negative: barIsNegative, 'first-of-pair': isFirstOfPair}}
      style={{'grid-column': barGridColumn}}
      attrs={{elref: `bar`}}
    >
      {...segments.map((barSegment, idx) => {
        const {widthAsPercentage} = getBarChartDisplayValues({
          value: barSegment.value,
          percent: barSegment.value / chartMax || 0,
          segmentMax,
          barValueType,
        });

        const segmentLabel = showSegmentLabels ? (
          <div sel='.bar-segment-label-wrapper'>
            {/* Empty div used to push inner label to the edge and overflow to next line when there isn't enough space */}
            {/* This way, we can hide overflowing labels completely using just CSS */}
            <div sel='.bar-segment-label-nudger' />
            <div sel='.bar-segment-label' attrs={{elref: `barSegmentLabel`}}>
              {barSegment.label}
            </div>
          </div>
        ) : null;

        return (
          <div
            sel='.bar-segment-wrapper'
            style={{
              width: `${widthAsPercentage * 100}%`,
            }}
            on={{
              mousemove: (ev) => mouseMove(ev, barSegment),
              mouseleave: mouseLeave,
              ...(enableClick ? {click: (ev) => clickBar(ev, bar, barSegment)} : {}),
            }}
            class={{
              clickable: enableClick,
            }}
            attrs={{elref: `barSegment`}}
          >
            <div
              sel='.bar-segment'
              style={{
                ...getBarBackground(barSegment.pattern, barSegment.color),
                width: `100%`,
              }}
            />
            {segmentLabel}
            {idx === segments.length - 1 && (
              <div sel='.bar-label' class={{negative: barIsNegative}} attrs={{elref: `barLabel`}}>
                {barLabel}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
